import React, { useEffect, useState } from 'react'
import { shape, func } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { get, isEmpty } from 'lodash'
import yn from 'yn'
import moment from 'moment'
import queryString from 'query-string'
// ************ CONSTANTS *************
import {
  defaultRouteImage,
  ModifyFlowType
} from '../../configuration/constants'
import appRoutes from '../../configuration/appRoutes'
// ************ ACTIONS *************
import {
  setDepartureDate,
  setDepartureRoute,
  setReturnDate,
  setReturnRoute,
  setRouteForm
} from '../../redux/ferryRouteSelections/actions'
import { fetchCrossing } from '../../redux/crossings/actions'
import * as UserSelectionActions from '../../redux/userSelections/actions'
// *********** COMPONENTS ***********
import Hero from '../../components/Hero/Hero'
import ferryRoutesForms from '../../configuration/ferryRoutesForms'
import FerryRoutesGroupings from '../../components/ferryRoutes/FerryRoutesGroupings'
import FerryRoutesFormsContainer from '../../components/ferryRoutes/FerryRoutesFormsContainer'
// ********* SELECTORS **************
import {
  selectConfigurationContent,
  selectConfigurationData,
  selectLabels,
  selectRoutesContent,
  selectServiceMessageContent
} from '../../redux/configuration/selectors'
import {
  selectActiveRouteForm,
  selectDepartureDate,
  selectDepartureRoute,
  selectReturnDate,
  selectReturnRoute
} from '../../redux/ferryRouteSelections/selectors'
import { selectModifyFlowType } from '../../redux/modifyBooking/selectors'
import { selectTravelAgentAccount } from '../../redux/travelAgent/selectors'
import { selectCustomerAccount } from '../../redux/user/selectors'
import selectFerryRoutesModifyPrefillData from '../../redux/selectors/selectFerryRoutesModifyPrefillData'
// *********** UTILITIES ************
import { isValidTripType } from '../../redux/vehicleSelections/utilities'
import filterGroupRoutes from '../../utilities/content/filterGroupRoutes'
// *********** STYLES ***************
import '../../styles/scss/components/ferryRoutes/ferry-routes.scss'
import '../../styles/scss/components/ferryRoutes/ferry-routes-form-defaults.scss'

const FerryRoutesContainer = ({
  handleRouteChange,
  openModal,
  closeModal,
  modalIsOpen
}) => {
  const dispatch = useDispatch()
  const queryParams = queryString.parse(window.location.search)
  const clientId = process.env.REACT_APP_FLOW_CLIENT_ID

  const departureRoute = useSelector(selectDepartureRoute)
  const returnRoute = useSelector(selectReturnRoute)
  const configuration = useSelector(selectConfigurationData)
  const activeRouteForm = useSelector(selectActiveRouteForm)
  const travelAgentAccount = useSelector(selectTravelAgentAccount)
  const customerAccount = useSelector(selectCustomerAccount)
  const content = useSelector(selectConfigurationContent)
  const routeContent = useSelector(selectRoutesContent)
  const serviceMessageContent = useSelector(selectServiceMessageContent)
  const labels = useSelector(selectLabels)
  const modifyFlowType = useSelector(selectModifyFlowType)
  const modifyPrefillData = useSelector(selectFerryRoutesModifyPrefillData)

  const [availabilityErrors, setAvailabilityErrors] = useState(null)
  const [crossingsLoading, setCrossingsLoading] = useState(false)
  const [activeRouteGrouping, setActiveRouteGrouping] = useState(null)
  const ferryParam = get(queryParams, 'ferry')
  const tripTypeParam = get(queryParams, 'tripType')

  const departureDate = useSelector(selectDepartureDate)
  const returnDate = useSelector(selectReturnDate)
  useEffect(() => {
    if (
      yn(process.env.REACT_APP_COMPONENT_ENABLE_ROUTE_GROUPINGS) &&
      ferryParam
    ) {
      toggleRouteGrouping(ferryParam)
      if (
        !modalIsOpen['ferry-service-message'] &&
        serviceMessageContent?.[ferryParam]
      ) {
        openModal({
          type: 'ferry-service-message',
          message: serviceMessageContent[ferryParam]
        })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      !isEmpty(tripTypeParam) &&
      isValidTripType(tripTypeParam.toUpperCase())
    ) {
      dispatch(setRouteForm(tripTypeParam.toUpperCase())) // TODO: Deprecate
      dispatch(UserSelectionActions.setTripType(tripTypeParam.toUpperCase()))
    }
  }, [tripTypeParam, dispatch])

  useEffect(() => setAvailabilityErrors(null), [departureDate])
  useEffect(() => setAvailabilityErrors(null), [returnDate])

  // pre-filling the form if we have an active modifying booking in state
  useEffect(() => {
    if (modifyPrefillData) {
      dispatch(setRouteForm(modifyPrefillData.tripType)) // TODO: Deprecate
      dispatch(UserSelectionActions.setTripType(modifyPrefillData.tripType))

      if (modifyPrefillData.departureRoute) {
        dispatch(setDepartureRoute(modifyPrefillData.departureRoute)) // TODO: Deprecate
        dispatch(
          UserSelectionActions.setDepartureRoute(
            modifyPrefillData.departureRoute.code
          )
        )
        setActiveRouteGrouping(modifyPrefillData.ferryId)
      }

      if (modifyPrefillData.returnRoute) {
        dispatch(setReturnRoute(modifyPrefillData.returnRoute)) // TODO: Deprecate
        dispatch(
          UserSelectionActions.setReturnRoute(
            modifyPrefillData.returnRoute.code
          )
        )
      } else {
        dispatch(setReturnRoute({})) // TODO: Deprecate
        dispatch(UserSelectionActions.setReturnRoute(null))
      }

      if (modifyPrefillData.departureDate) {
        const departureMoment = moment(modifyPrefillData.departureDate)
        dispatch(setDepartureDate(departureMoment)) // TODO: Deprecate
        dispatch(
          UserSelectionActions.setDepartureSearchDate(
            departureMoment.format('YYYY-MM-DD')
          )
        )
      }

      if (modifyPrefillData.returnDate) {
        const returnMoment = moment(modifyPrefillData.returnDate)
        dispatch(setReturnDate(moment(modifyPrefillData.returnDate))) // TODO: Deprecate
        dispatch(
          UserSelectionActions.setReturnSearchDate(
            returnMoment.format('YYYY-MM-DD')
          )
        )
      } else {
        dispatch(setReturnDate(null)) // TODO: Deprecate
        dispatch(UserSelectionActions.setReturnSearchDate(null))
      }
    }
  }, [dispatch, modifyPrefillData])

  const getDepartureErrorNode = () => {
    let departureErrorNode = <span>{labels.noDepartureCrossingsAvailable}</span>

    return departureErrorNode
  }

  const getReturnErrorNode = () => {
    let returnErrorNode = <span>{labels.noReturnCrossingsAvailable}</span>

    return returnErrorNode
  }

  const getErrorNodesByClient = (crossingErrors = {}) => {
    const { departureError, returnError } = crossingErrors
    let result = []
    let departureErrorNode = departureError ? getDepartureErrorNode() : null
    let returnErrorNode = returnError ? getReturnErrorNode() : null
    if (departureErrorNode) result.push(departureErrorNode)
    if (returnErrorNode) result.push(returnErrorNode)

    return result
  }

  const determineErrorsOnSubmit = ({
    departureCrossings = {},
    returnCrossings = {},
    isReturnTrip
  }) =>
    getErrorNodesByClient({
      departureError: !Object.keys(departureCrossings).length,
      returnError: isReturnTrip && !Object.keys(returnCrossings).length
    })

  const getCrossingData = async (direction = '') => {
    const options = {
      fetchAllPassengerTypes: true,
      validationKey:
        direction === 'departure'
          ? 'ferryRoutesDepartureCrossing'
          : 'ferryRoutesReturnCrossing'
    }
    const responseData = await dispatch(fetchCrossing(direction, options))

    // NOTE(ebarrett): Will eventually replace fetchCrossing call above
    // const { departureParams, returnParams } = fetchCrossingsParams
    // const params = direction === 'departure' ? departureParams : returnParams
    // dispatch(fetchCrossingsWithParams(params, direction, options))

    return responseData.isSuccess
      ? get(responseData, 'payload.crossings', {})
      : {}
  }

  const getReturnCrossingResponse = async () => {
    let result
    if (activeRouteForm === 'RT') result = await getCrossingData('return')
    return result
  }

  const submit = async () => {
    setCrossingsLoading(true)
    setAvailabilityErrors(null)

    const departureResponse = await getCrossingData('departure')
    const returnResponse = await getReturnCrossingResponse()
    const errors = determineErrorsOnSubmit({
      departureCrossings: departureResponse,
      returnCrossings: returnResponse,
      isReturnTrip: activeRouteForm === 'RT'
    })

    if (errors.length > 0) {
      setCrossingsLoading(false)
      setAvailabilityErrors(errors)
    } else {
      handleRouteChange(appRoutes.passengerQuantities.pathname, {
        isFromRouteSelection: true
      })
    }
  }

  let ferryRoutes = []
  let isPairRoutes = false

  if (configuration?.ferry) {
    if (
      configuration.ferry.options &&
      !isEmpty(configuration.ferry.options.routes)
    ) {
      if (
        yn(process.env.REACT_APP_COMPONENT_ENABLE_ROUTE_GROUPINGS) &&
        activeRouteGrouping
      ) {
        ferryRoutes = filterGroupRoutes(
          configuration.ferry.options.routes,
          activeRouteGrouping
        )
      } else {
        ferryRoutes = configuration.ferry.options.routes
      }
    }

    if (!isEmpty(ferryRoutes) && ferryRoutes.length === 2) {
      isPairRoutes = true
    }
  }

  let renderFormToggles = []
  let routeFormElement = null

  const activeRouteContent =
    departureRoute && departureRoute.code && routeContent?.length
      ? routeContent.find(route => route.code === departureRoute.code)
      : null

  for (const formKey in ferryRoutesForms) {
    const currentForm = ferryRoutesForms[formKey]

    if (currentForm.labelKey && currentForm.type && currentForm.component) {
      if (modifyFlowType !== ModifyFlowType.DEPARTURE_LOCKED) {
        renderFormToggles.push(
          <div
            key={'form-toggle-' + currentForm.type}
            className={`theme-title-variant-six ferry-routes__form-type-toggle${
              currentForm.type === activeRouteForm ? ' active' : ''
            }`}
            onClick={() => {
              dispatch(setRouteForm(currentForm.type)) // TODO: Deprecate
              dispatch(setDepartureRoute({})) // TODO: Deprecate
              dispatch(setReturnRoute({})) // TODO: Deprecate
              dispatch(UserSelectionActions.setTripType(currentForm.type))
              dispatch(
                UserSelectionActions.setDepartureRoute(departureRoute.code)
              )
              dispatch(UserSelectionActions.setReturnRoute(returnRoute.code))
            }}
            tabIndex={0}
          >
            {labels[currentForm.labelKey]}
          </div>
        )
      }

      if (currentForm.type === activeRouteForm) {
        routeFormElement = (
          <currentForm.component
            openModal={openModal}
            closeModal={closeModal}
            modalIsOpen={modalIsOpen}
            ferryRoutes={ferryRoutes}
            isPairRoutes={isPairRoutes}
            submit={submit}
            availabilityErrors={clientId === 'mai' && availabilityErrors}
            enableRouteSelection={
              modifyFlowType !== ModifyFlowType.DEPARTURE_LOCKED
            }
          />
        )
      }
    }
  }

  // FIXED: added beautiful image, with fallback
  let routeMap = get(content, 'defaultRouteMapImage.path', defaultRouteImage)

  if (
    configuration?.ferry?.options?.routes.length &&
    departureRoute &&
    departureRoute.code
  ) {
    routeMap = activeRouteContent
      ? get(activeRouteContent, 'routeMapImage.path', defaultRouteImage)
      : defaultRouteImage
  }

  const routesGroupings = get(content, 'routesGroupings', [])

  const toggleRouteGrouping = (
    routeGroup,
    isClickEvent,
    setDefaults = true
  ) => {
    const groupRoutes = filterGroupRoutes(
      configuration.ferry.options.routes,
      routeGroup
    )

    if (groupRoutes) {
      setActiveRouteGrouping(routeGroup)

      if (isClickEvent && groupRoutes[0]) {
        if (setDefaults) {
          dispatch(setDepartureRoute(groupRoutes[0])) // TODO: Deprecate
          dispatch(UserSelectionActions.setDepartureRoute(groupRoutes[0].code))
        } else {
          dispatch(setDepartureRoute({})) // TODO: Deprecate
          dispatch(UserSelectionActions.setDepartureRoute(null))
        }
      }

      if (isClickEvent && groupRoutes[1]) {
        if (setDefaults) {
          dispatch(setReturnRoute(groupRoutes[1])) // TODO: Deprecate
          dispatch(UserSelectionActions.setReturnRoute(groupRoutes[1].code))
        } else {
          dispatch(setReturnRoute({})) // TODO: Deprecate
          dispatch(UserSelectionActions.setReturnRoute(null))
        }
      } else if (isClickEvent || !setDefaults) {
        dispatch(setReturnRoute({})) // TODO: Deprecate
        dispatch(UserSelectionActions.setReturnRoute(null))
      }
    }
  }

  const formsContainerCommonProps = {
    clientId,
    availabilityErrors,
    crossingsLoading,
    customerAccount,
    travelAgentAccount,
    labels,
    openModal,
    renderFormToggles,
    routeFormElement,
    routeMap,
    handleRouteChange,
    modifyFlowType
  }

  return (
    <div className="ferry-routes">
      {yn(process.env.REACT_APP_COMPONENT_HERO) && <Hero content={content} />}

      {yn(process.env.REACT_APP_COMPONENT_ENABLE_ROUTE_GROUPINGS) &&
        !isEmpty(routesGroupings) && (
          <FerryRoutesGroupings
            serviceMessageContent={serviceMessageContent}
            activeRouteGrouping={activeRouteGrouping}
            labels={labels}
            openModal={openModal}
            closeModal={closeModal}
            modalIsOpen={modalIsOpen}
            routesGroupings={routesGroupings}
            toggleRouteGrouping={toggleRouteGrouping}
          />
        )}

      {yn(process.env.REACT_APP_COMPONENT_ENABLE_ROUTE_GROUPINGS) ? (
        activeRouteGrouping ? (
          <FerryRoutesFormsContainer
            activeRouteGrouping={activeRouteGrouping}
            {...formsContainerCommonProps}
          />
        ) : null
      ) : (
        <FerryRoutesFormsContainer {...formsContainerCommonProps} />
      )}
    </div>
  )
}

FerryRoutesContainer.propTypes = {
  handleRouteChange: func.isRequired,
  openModal: func.isRequired,
  closeModal: func.isRequired,
  modalIsOpen: shape().isRequired
}

export default FerryRoutesContainer
